// Historial de auditoría de un disco (Tarea 19A, Req. 10.11).
//
// Los cambios se AGRUPAN por operación: cada registro muestra su fecha y el
// número de campos modificados; al hacer click se despliega el detalle campo a
// campo (old→new) de esa operación, con la opción de revertir cada campo si el
// usuario tiene permiso de sincronización. La sección también expone las fechas
// de catalogado y de última actualización del disco.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { useAsync } from '../ui/useAsync.js';
import { AlertMessage, AsyncBoundary, ButtonSpinner, Card, Icon } from '../ui/components.js';
import { fieldLabel } from './syncFieldLabels.js';
import type { RecordChange } from '../types.js';

interface ChangeGroup {
  /** Clave estable del grupo (sync_job_id o timestamp del lote manual). */
  key: string;
  /** Momento del cambio (ISO) para ordenar y mostrar. */
  when: string | null;
  /** Origen del cambio (discogs / manual / ...), tomado del primer ítem. */
  source: string;
  /** Cambios campo a campo de esta operación. */
  items: RecordChange[];
}

/**
 * Agrupa los cambios en OPERACIONES. Los de sincronización comparten
 * `sync_job_id`; los manuales (sin job) se agrupan por su marca de tiempo (los
 * de una misma edición comparten `created_at`). Devuelve los grupos ordenados
 * del más reciente al más antiguo.
 */
function groupChanges(items: RecordChange[]): ChangeGroup[] {
  const byKey = new Map<string, ChangeGroup>();
  for (const c of items) {
    const key = c.sync_job_id ?? `manual:${c.created_at ?? c.id}`;
    let group = byKey.get(key);
    if (!group) {
      group = { key, when: c.created_at ?? null, source: c.source, items: [] };
      byKey.set(key, group);
    }
    group.items.push(c);
    // Conserva el timestamp más reciente del grupo (por si difieren).
    if (c.created_at && (!group.when || c.created_at > group.when)) group.when = c.created_at;
  }
  return [...byKey.values()].sort((a, b) => (b.when ?? '').localeCompare(a.when ?? ''));
}

/** Etiqueta legible del origen de un cambio para el historial. */
function sourceLabel(t: TFunction, source: string): string {
  // Si no hay traducción específica para el origen, se muestra el valor crudo.
  return t(`recordChanges.source.${source}`, { defaultValue: source });
}

export function RecordChangesPanel({
  recordId,
  canRevert,
  cataloguedAt,
  updatedAt,
  refreshKey,
}: {
  recordId: string;
  canRevert: boolean;
  cataloguedAt?: string;
  updatedAt?: string;
  /**
   * Cambia (lo incrementa el contenedor) tras aplicar una sincronización u otra
   * operación que genere historial, para forzar la recarga del panel sin esperar
   * a un refresco de página. Opcional para no romper otros usos del componente.
   */
  refreshKey?: number;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const changes = useAsync(() => api.records.changes(recordId), [recordId, refreshKey]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function revert(changeId: string) {
    setBusy(changeId);
    setError(null);
    try {
      await api.sync.revertChange(changeId);
      changes.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('recordChanges.errRevert'));
    } finally {
      setBusy(null);
    }
  }

  const groups = useMemo(
    () => (changes.data ? groupChanges(changes.data.items) : []),
    [changes.data],
  );

  return (
    <Card title={t('recordChanges.title')} icon="history" collapsible defaultOpen={false}>
      {/* Fechas de catalogación y última actualización del disco */}
      <div className="grid grid-cols-2 gap-space-sm mb-space-sm">
        <div className="surface-rack-card !p-space-sm flex flex-col gap-0.5">
          <span className="section-label text-on-surface-variant">{t('recordChanges.catalogued')}</span>
          <span className="font-code-catalog text-body-sm text-on-surface">{formatDate(cataloguedAt)}</span>
        </div>
        <div className="surface-rack-card !p-space-sm flex flex-col gap-0.5">
          <span className="section-label text-on-surface-variant">{t('recordChanges.lastUpdate')}</span>
          <span className="font-code-catalog text-body-sm text-on-surface">{formatDate(updatedAt)}</span>
        </div>
      </div>

      <AlertMessage message={error} className="mb-space-sm" />

      <AsyncBoundary state={changes} empty={(d) => d.items.length === 0}>
        {() => (
          <ul className="flex flex-col gap-space-sm">
            {groups.map((g) => {
              const open = expanded.has(g.key);
              return (
                <li key={g.key} className="surface-rack-card !p-0 overflow-hidden">
                  {/* Cabecera del registro: fecha + nº de cambios (click para expandir) */}
                  <button
                    type="button"
                    onClick={() => toggle(g.key)}
                    className="w-full flex items-center justify-between gap-space-sm p-space-sm text-left"
                  >
                    <span className="flex items-center gap-space-xs min-w-0">
                      <Icon
                        name={open ? 'expand_more' : 'chevron_right'}
                        className="text-[18px] text-on-surface-variant shrink-0"
                      />
                      <span className="flex flex-col min-w-0">
                        <span className="font-body-md text-body-md text-on-surface truncate">
                          {formatDateTime(g.when)}
                        </span>
                        <span className="font-body-sm text-body-sm text-on-surface-variant">
                          {g.items.length} {g.items.length === 1 ? t('recordChanges.change') : t('recordChanges.changes')}
                        </span>
                      </span>
                    </span>
                    <span className="px-2 py-0.5 rounded bg-surface-container text-on-surface-variant font-badge-micro text-badge-micro uppercase shrink-0">
                      {sourceLabel(t, g.source)}
                    </span>
                  </button>

                  {/* Detalle campo a campo (solo si está expandido) */}
                  {open && (
                    <ul className="flex flex-col divide-y divide-border-subtle border-t border-border-subtle">
                      {g.items.map((c) => (
                        <li key={c.id} className="flex flex-col gap-space-xs p-space-sm">
                          <div className="flex items-center justify-between gap-space-sm">
                            <span className="flex items-center gap-space-xs font-headline-sm text-body-sm text-goldmine-vg">
                              <Icon name="edit" className="text-[15px] text-primary" />
                              {fieldLabel(t, c.field)}
                            </span>
                            {canRevert && (
                              <button
                                className="btn-secondary btn-sm"
                                type="button"
                                disabled={busy === c.id}
                                onClick={() => void revert(c.id)}
                              >
                                {busy === c.id ? (
                                  <ButtonSpinner className="text-[14px]" />
                                ) : (
                                  <Icon name="undo" className="text-[15px]" />
                                )}
                                {busy === c.id ? t('recordChanges.reverting') : t('recordChanges.revert')}
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-space-xs font-code-catalog text-body-sm">
                            <span className="text-on-surface-variant line-through break-words">{c.old_value ?? '—'}</span>
                            <Icon name="arrow_forward" className="text-[14px] text-on-surface-variant shrink-0" />
                            <span className="text-goldmine-mint break-words">{c.new_value ?? '—'}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </AsyncBoundary>
    </Card>
  );
}

/** Fecha corta legible (locale del navegador), o guion. */
function formatDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/** Fecha + hora legible (locale del navegador), o guion. */
function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}
